import { useEffect, useState } from 'react';
import { getTrackedQuantityCheck } from '../api.js';
import type {
  TrackedQuantityAnchorView,
  TrackedQuantityCheckView,
  TrackedQuantityRowView,
} from '../api.js';

/**
 * The design-review register's Tracked Quantities, checked against every priced issue (ADR-0069
 * A3 S8). The SERVER computes every reading from canon's thresholds v1.1; this only says it.
 *
 * - A row past its band is loud: canon routes it to the precon manager.
 * - A row whose reading differs from the register's own flag is loud too: canon makes that a finding.
 * - Every row the check could not compute says why. A blank would read as "within", and most real rows
 *   are not computed (a change in kind, a value stated as two figures, an issue with no snapshot).
 *
 * Staff only, with the Drawings tab: the register is GGP's working record.
 */

function pct(f: number): string {
  return `${f > 0 ? '+' : ''}${(f * 100).toFixed(1)}%`;
}

function signed(n: number): string {
  return `${n > 0 ? '+' : ''}${n.toLocaleString('en-US')}`;
}

/** One computed comparison against one priced issue, as a sentence, with the issue's own value. */
function against(a: TrackedQuantityAnchorView): string | null {
  const r = a.result;
  const at = a.anchorValue === null ? a.issue : `${a.issue} (${a.anchorValue})`;
  if (r.outcome === 'changed_mix') {
    return `The mix moved against ${at}. Canon’s ±2% does not say whether it is of each type or the total, so the reviewer reads it.`;
  }
  if (r.outcome === 'unchanged_mix') return `The mix is the same as at ${at}.`;
  if (r.outcome === 'not_computed' || typeof r.delta !== 'number') return null;
  const move =
    r.delta === 0
      ? 'no change'
      : `${signed(r.delta)}${r.deltaPct == null ? '' : ` (${pct(r.deltaPct)})`}`;
  // Accessibility counts move on any change, so they have no band to be inside or past.
  if (!r.band) {
    return r.outcome === 'substantial'
      ? `Changed against ${at}: ${move}.`
      : `Unchanged against ${at}.`;
  }
  const band = `the ±${String(r.band * 100)}% band`;
  return r.outcome === 'substantial'
    ? `Past ${band} against ${at}: ${move}.`
    : `Within ${band} against ${at}: ${move}.`;
}

function reading(row: TrackedQuantityRowView): string {
  const latest = row.anchors.at(-1);
  switch (row.outcome) {
    case 'reviewer_judges':
      return 'A change in kind is the reviewer’s call, not a percentage.';
    case 'no_band':
      return 'Canon tracks this quantity with no band.';
    case 'not_canon_class':
      return 'Not one of the quantities canon tracks.';
    case 'substantial': {
      // Every priced issue it is past, then the latest one if that is not among them.
      const past = row.anchors.filter(
        (a) => a.result.outcome === 'substantial' || a.result.outcome === 'changed_mix',
      );
      const shown = latest && !past.includes(latest) ? [...past, latest] : past;
      const lines = shown.map(against).filter((l): l is string => l !== null);
      return `${lines.join(' ')} This goes to the precon manager.`;
    }
    case 'within':
      return (latest && against(latest)) ?? '';
    case 'not_computed': {
      if (!latest) return 'No priced issue on this record yet.';
      switch (latest.result.reason) {
        case 'no_snapshot':
          return `${latest.issue} was priced but recorded no quantities.`;
        case 'not_in_snapshot':
          return `The quantities recorded at ${latest.issue} do not name this row.`;
        case 'anchor_unread':
          return `The value recorded at ${latest.issue} is not one figure.`;
        case 'current_unread':
          return 'The current value is not one figure.';
        case 'mix_shape_differs':
          return `The mix is stated in a different shape at ${latest.issue}.`;
        default:
          return 'Not computed.';
      }
    }
  }
}

function flagText(row: TrackedQuantityRowView): string {
  if (row.registerFlag === 'substantial') return 'Substantial';
  if (row.registerFlag === 'not_substantial') return 'Not flagged';
  return 'Not stated';
}

export function TrackedQuantitiesPanel({
  view,
}: {
  view: { loaded: boolean; check: TrackedQuantityCheckView | null } | null;
}) {
  if (view === null) return <p className="muted">Loading…</p>;
  if (!view.loaded) {
    return (
      <p className="card__note" role="note">
        The tracked quantities could not be loaded.
      </p>
    );
  }
  const check = view.check;
  if (check === null) {
    return (
      <p className="muted">No design-review register with tracked quantities is on this record.</p>
    );
  }
  const latest = check.pricedAnchors.at(-1);
  const computed = check.rows.filter((r) => r.outcome === 'substantial' || r.outcome === 'within');
  return (
    <>
      <p className="card__note">
        {latest === undefined
          ? 'No issue on this register is marked priced, so nothing is compared yet.'
          : `Compared against every priced issue; the latest is ${latest}. ${String(computed.length)} of ${String(check.rows.length)} quantities could be computed.`}
        {check.registerThresholds !== null && check.registerThresholds !== check.thresholds
          ? ` The register states thresholds ${check.registerThresholds}; these are read at ${check.thresholds}.`
          : ''}
      </p>
      <table className="tq__table" aria-label="Tracked quantities against the priced issues">
        <thead>
          <tr>
            <th>Quantity</th>
            <th>Current</th>
            <th>Our reading</th>
            <th>Register’s flag</th>
          </tr>
        </thead>
        <tbody>
          {check.rows.map((row) => (
            <tr
              key={row.key}
              className={
                row.outcome === 'substantial' || row.disagreesWithRegister
                  ? 'tq__row--flag'
                  : undefined
              }
            >
              <td>{row.q}</td>
              <td>{row.current ?? 'Not stated'}</td>
              <td>{reading(row)}</td>
              <td>
                {flagText(row)}
                {row.disagreesWithRegister ? (
                  <strong role="note"> Differs from our reading.</strong>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {check.droppedRows > 0 && (
        <p className="card__note muted">
          {check.droppedRows === 1
            ? 'One quantity from an earlier register is no longer in the table.'
            : `${String(check.droppedRows)} quantities from earlier registers are no longer in the table.`}
        </p>
      )}
      {check.missingRows.length > 0 && (
        <p className="card__note" role="note">
          The table names {String(check.missingRows.length)} quantities the record does not hold.
        </p>
      )}
    </>
  );
}

/** The fetching wrapper. `getTrackedQuantityCheck` never rejects: a failure arrives as `loaded: false`. */
export function TrackedQuantities({ code }: { code: string }) {
  const [view, setView] = useState<{
    loaded: boolean;
    check: TrackedQuantityCheckView | null;
  } | null>(null);
  useEffect(() => {
    let live = true;
    void getTrackedQuantityCheck(code).then((v) => {
      if (live) setView(v);
    });
    return () => {
      live = false;
    };
  }, [code]);
  return (
    <section className="card">
      <h3 className="card__title">Tracked quantities</h3>
      <TrackedQuantitiesPanel view={view} />
    </section>
  );
}
